import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, UrlSegment, RouterLink, RouterOutlet } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, map, startWith, tap } from 'rxjs/operators';
import { GenericHttpService } from '../../global-services/generic-http.service';
import { HttpParams } from '@angular/common/http';
import { UrlConstants } from '../../utilities/url-constants';
import {
  GenericPaginatedData,
  GenericResponse,
  PatientDetails,
  MenuItem,
  EstimateView,
  ICustomMenuHeader,
} from '../../utilities/data-models';
import { ApplicationInsightsService } from '../../global-services/azure/insights-service/application-insights.service';
import {
  InvoiceTypeEnum,
  ModulesEnum,
  ProjectUtils,
  SeverityLevel,
} from '../../utilities/project-utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProjectDataService } from '../../global-services/project-data.service';
import { Subscription } from 'rxjs';
import { MenuItemsService } from '../menu-items.service';
import { FilterPipe } from 'src/app/utilities/common-pipes/filter.pipe';
import { AccessPermissionsService } from '../access-permissions.service';
import { MatDialog } from '@angular/material/dialog';
import { NotpadModalComponent } from './notpad-modal/notpad-modal.component';
import { environment } from '../../../environments/environment';
import { CustomApiCacheService } from '../../global-services/custom-api-cache.service';
import { MatSidenavModule } from '@angular/material/sidenav';
import { NgClass, NgIf, NgFor, UpperCasePipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { QuicklinkModule } from 'ngx-quicklink';
import { AppLoaderService } from 'src/app/global-services/app-loader/app-loader.service';
import { PatientService } from 'src/app/views/patient/patient.service';
import { ChatWidgetComponent } from '../../acs-chat/components/chat-widget/chat-widget';
import { ChatStateService } from '../../acs-chat/services';
import { CustomStorageService } from '../../global-services/custom-storage.service';
interface InvoiceSearch {
  Id: number;
  PatientDetailsId: number;
  InvoiceTypeId: number;
  ChildId?: number;
  ChildRecordId?: string;
  InvoiceRecordId: number;
}

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  providers: [FilterPipe],
  standalone: true,
  imports: [
    QuicklinkModule,
    MatSidenavModule,
    NgClass,
    RouterLink,
    NgIf,
    MatTooltipModule,
    MatIconModule,
    MatMenuModule,
    NgFor,
    RouterOutlet,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatInputModule,
    ReactiveFormsModule,
    UpperCasePipe,
    ChatWidgetComponent,
  ],
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  moduleToDoTypeVal = ModulesEnum.ToDO;
  moduleCheckListTypeVal = ModulesEnum.Checklists;
  moduleTypePatientVal = ModulesEnum.Patient;
  isToDoPermissionAllow = true;
  isCheckListPermissionAllow = true;
  isPatientPermissionAllow = true;
  isPatientDashboardViewAllow = true;
  showSideNav = false;
  hasSubMenu = false;
  backgroundClass = 'pink-bg';

  headerData: ICustomMenuHeader;
  headerPageUserName: string = undefined;
  userImage: string =
    'assets/images/system-mgnt/users/avatar-child-face-thumbnail.png';
  myActionNotificationCount: string;
  engagementNotificationCount: string;
  checklistNotificationsCount: string;
  patientActionNotificationCount: string;

  menuItems: MenuItem[] = [];
  flattenedMenuItems: MenuItem[] = [];
  menuItemsForSearch: MenuItem[] = [];

  patientAutoCompleteFormCtrl = new FormControl();
  patientAutoCompleteSearchSubscription: Subscription;
  patientRecords: PatientDetails[] = [];
  searchingPatientRecordsLoading = false;

  patientRemainingCount = 0;
  patientPageNumber = 1;
  patientPageSize = 10;
  patientSearchTerm = '';

  menuHeaderSubscription: Subscription;
  subMenuHeaderSubscription: Subscription;
  getPatientStatusClass = ProjectUtils.getPatientStatusClass;

  nameSearch: string;

  value = 'Clear me';

  version = '0.0.0';
  isPWAInstallReady = false;

  currentDate = ProjectUtils.getDayJSDateObject().format('YYYY-MM-DD');
  helpCentreURL: string;

  invoiceSummaries: EstimateView[] = [];
  filteredInvoiceSummaries: InvoiceSearch[] = [];
  totalInvoiceItems: number;
  invoiceSearch: string;

  isRefreshingCache = false;

  showChatWidget = false;
  chatUserId: string = '';
  chatUserName: string = '';
  chatUnreadCount = 0;
  isInstantMessagingEnabled = false;
  private chatStateService = inject(ChatStateService);

  menuChangesSubscription: Subscription;
  notificationCountSubscription: Subscription;
  userDetailsSubscription: Subscription;
  routeEventsSubscription: Subscription;
  userNameSubscription: Subscription;
  pwaReadySubscription: Subscription;
  subHeader: string | undefined = undefined;

  constructor(
    private router: Router,
    private httpService: GenericHttpService,
    private insightService: ApplicationInsightsService,
    private snackBar: MatSnackBar,
    private projectDataService: ProjectDataService,
    private cacheService: CustomApiCacheService,
    private menuItemsService: MenuItemsService,
    private accessPermissionsService: AccessPermissionsService,
    private cdr: ChangeDetectorRef,
    private matDialog: MatDialog,
    private loader: AppLoaderService,
    private patientService: PatientService,
    private customStorageService: CustomStorageService
  ) {}

  ngOnInit(): void {
    this.version = environment.version;

    this.initUsernameChange();
    this.initPWAInstallChange();
    this.initMenuChange();
    this.initPatientSearch();
    this.initNotificationCount();
    this.initUserDetailsForProfilePic();
    this.initRouteChangeEvents();
    this.initCustomMenuNameEvents();
    this.initPermissionVariables();
    this.loadDPRCalendarFix();

    //get the url for help centre URL
    //this.helpCentreURL = 'https://capsclinic.sharepoint.com/sites/Capstone2.0HelpCentre';
    //this.helpCentreURL = 'https://capstechnology.atlassian.net/wiki/spaces/CAPS/overview';
    this.helpCentreURL =
      'https://capstechnology.atlassian.net/servicedesk/customer/portals';

    // this.subscribeLetterInCount();
  }

  subscribeLetterInCount() {
    /*this.projectDataService.letterInUploadCountSubject.subscribe((res) => {
      this.incomingPatientActionsTotalCount = res;

    })*/
  }

  ngOnDestroy() {
    if (this.userNameSubscription) {
      this.userNameSubscription.unsubscribe();
    }
    if (this.pwaReadySubscription) {
      this.pwaReadySubscription.unsubscribe();
    }
    if (this.menuChangesSubscription) {
      this.menuChangesSubscription.unsubscribe();
    }
    if (this.patientAutoCompleteSearchSubscription) {
      this.patientAutoCompleteSearchSubscription.unsubscribe();
    }
    if (this.notificationCountSubscription) {
      this.notificationCountSubscription.unsubscribe();
    }
    if (this.userDetailsSubscription) {
      this.userDetailsSubscription.unsubscribe();
    }
    if (this.routeEventsSubscription) {
      this.routeEventsSubscription.unsubscribe();
    }
    if (this.menuHeaderSubscription) {
      this.menuHeaderSubscription.unsubscribe();
    }
    if (this.unreadPollInterval) {
      clearInterval(this.unreadPollInterval);
    }
    this.menuItemsService.setMenuHeader(null);
  }

  initMenuChange() {
    if (this.menuChangesSubscription) {
      this.menuChangesSubscription.unsubscribe();
    }
    this.menuChangesSubscription =
      this.menuItemsService.menuItemsSubject.subscribe((result) => {
        if (result) {
          this.menuItems = result;
          this.flattenedMenuItems = this.flattenMenuItems(
            this.menuItems,
            undefined
          );
          this.cdr.detectChanges();
        }
      });
  }

  initPWAInstallChange() {
    if (this.pwaReadySubscription) {
      this.pwaReadySubscription.unsubscribe();
    }
    this.pwaReadySubscription =
      this.projectDataService.modalPWAReadySubject.subscribe((res) => {
        // write now install only for android and browsers
        this.isPWAInstallReady =
          res &&
          this.projectDataService.modalPwaPlatform &&
          this.projectDataService.modalPwaPlatform !== 'IOS';
        this.cdr.detectChanges();
      });
  }

  initUsernameChange() {
    if (this.userNameSubscription) {
      this.userNameSubscription.unsubscribe();
    }
    this.userNameSubscription =
      this.menuItemsService.loggedInUsernameSubject.subscribe((res) => {
        if (res) {
          this.headerPageUserName = ' - ' + res;
          this.cdr.detectChanges();
        }
      });
  }

  initPermissionVariables() {
    //check for permission
    this.isToDoPermissionAllow =
      !this.accessPermissionsService.checkPermissions(
        this.moduleToDoTypeVal,
        'isPermissionAllow'
      );
    //check for CheckList permission
    this.isCheckListPermissionAllow =
      !this.accessPermissionsService.checkPermissions(
        this.moduleCheckListTypeVal,
        'isPermissionAllow'
      );

    this.isPatientPermissionAllow =
      !this.accessPermissionsService.checkPermissions(
        this.moduleTypePatientVal,
        '1'
      );

    this.isPatientDashboardViewAllow =
      !this.accessPermissionsService.checkPermissions(
        this.moduleTypePatientVal,
        'All'
      );

    this.cdr.detectChanges();
  }

  initPatientSearch() {
    if (this.patientAutoCompleteSearchSubscription) {
      this.patientAutoCompleteSearchSubscription.unsubscribe();
    }
    this.patientAutoCompleteSearchSubscription =
      this.patientAutoCompleteFormCtrl.valueChanges
        .pipe(
          startWith(''),
          debounceTime(300),
          tap(() => (this.searchingPatientRecordsLoading = true)),
          map((value: string) => {
            this.clearItem();
            if (value && value.length > 2) {
              if (value.includes('$')) {
                if (value.length > 3) {
                  this.invoiceSearch = value;
                  this.getInvoiceSummaries(value);
                }
              } else {
                this.nameSearch = value;
                this.filterPatientRecords(value);
                this.filterMenuItems(value);
              }
            }
            this.cdr.detectChanges();
          })
        )
        .subscribe();
  }

  navigateToInvoiceSummariesScreen(item: InvoiceSearch) {
    let routeName = 'estimate';
    if (item.InvoiceTypeId === InvoiceTypeEnum.Invoice) {
      routeName = 'invoice';
    }
    ProjectUtils.openInANewTab(
      `/patient/${item.PatientDetailsId}/finance/${routeName}/${item.Id}?InvoiceDetailsId=${item.ChildId}`,
      this.router
    );
  }

  setMenuHeaderAndBg(urlSegments: UrlSegment[]) {
    if (urlSegments[0].path === 'patient' || urlSegments[0].path === 'todo') {
      this.setBackgroundColor('blue-bg');
    } else {
      const completeUrlId =
        '/' + urlSegments.map((item) => item.path).join('/');

      let bestMatch = null;
      let bestMatchLength = 0;

      for (const menuItem of this.flattenedMenuItems) {
        if (completeUrlId.startsWith(menuItem.routeCompletePath)) {
          if (menuItem.routeCompletePath.length > bestMatchLength) {
            bestMatch = menuItem;
            bestMatchLength = menuItem.routeCompletePath.length;
          }
        }
      }

      if (bestMatch) {
        this.toggleMenuItem(bestMatch);
        this.setBackgroundColor(this.getBackgroundClass(bestMatch));
        this.menuItemsService.setMenuHeader({
          headerText: bestMatch.displayName,
          patientData: undefined,
        });
        this.hasSubMenu = bestMatch.treePath.length > 0;
      }
    }
    this.cdr.detectChanges();
  }

  onMenuClick(item: MenuItem) {
    if (!item.ignoreHeader) {
      this.menuItemsService.setMenuHeader({
        headerText: item.displayName,
        patientData: undefined,
      });
    }

    this.toggleMenuItem(item);
    item.clicked = true;
    this.hasSubMenu = !!item.subMenu;

    if (item.routeCompletePath === '/search') {
      this.showSideNav = !this.showSideNav;
    } else if (!item.subMenu) {
      this.loader.open();
      this.router.navigateByUrl(`${item.routeCompletePath}`);
      this.loader.close();
    }
    this.cdr.detectChanges();
  }

  onSubMenuClick(item: MenuItem, subMenuItem: MenuItem, event: MouseEvent) {
    subMenuItem.clicked = true;
    item.subMenu.forEach((obj) => {
      if (subMenuItem.id !== obj.id) {
        obj.clicked = false;
      }
    });
    event.stopPropagation();
    if (!subMenuItem.subMenu) {
      if (!subMenuItem.ignoreHeader) {
        this.menuItemsService.setMenuHeader({
          headerText: subMenuItem.displayName,
          patientData: undefined,
        });
      }
      this.loader.open();
      this.router
        .navigateByUrl(`${subMenuItem.routeCompletePath}`)
        .then(() => {
          this.loader.close();
        })
        .finally(() => {
          this.loader.close();
        });
    }
    this.cdr.detectChanges();
  }

  onLevel3MenuClick(
    item: MenuItem,
    subMenuItem: MenuItem,
    level3MenuItem: MenuItem,
    event: MouseEvent
  ) {
    item.subMenu.forEach((obj) => {
      obj.clicked = false;

      if (obj.subMenu) {
        obj.subMenu.forEach((nestedObj) => (nestedObj.clicked = false));
      }
    });
    subMenuItem.clicked = true;
    level3MenuItem.clicked = true;
    if (!level3MenuItem.ignoreHeader) {
      this.menuItemsService.setMenuHeader({
        headerText: level3MenuItem.displayName,
        patientData: undefined,
      });
    }

    event.stopPropagation();
    // this.router.navigateByUrl(`${level3MenuItem.routeCompletePath}`);
    this.loader.open();
    this.router
      .navigate([level3MenuItem.routeCompletePath])
      .then(() => {
        this.loader.close();
      })
      .finally(() => {
        this.loader.close();
      });
    this.cdr.detectChanges();
  }

  private setBackgroundColor(backgroundClass: string) {
    if (backgroundClass) {
      this.backgroundClass = backgroundClass;
    }
    this.cdr.detectChanges();
  }

  private filterPatientRecords(value: any): void {
    if (typeof value === 'string' && value !== '') {
      this.patientSearchTerm = value;
      const httpParams = new HttpParams()
        .set('st', value)
        .set('pn', this.patientPageNumber)
        .set('ps', this.patientPageSize);
      this.httpService
        .get<GenericResponse<GenericPaginatedData<PatientDetails>>>(
          UrlConstants.patientDetails,
          httpParams
        )
        .subscribe(
          (res) => {
            this.searchingPatientRecordsLoading = false;
            this.patientRecords = res.Result.ItemRecords;
            this.patientRemainingCount = res.Result.TotalCount;
            this.cdr.detectChanges();
          },
          (error) => {
            this.insightService.logException(
              error,
              'main-layout.component',
              UrlConstants.patientDetails,
              '136',
              SeverityLevel.Error
            );
            this.searchingPatientRecordsLoading = false;
            this.snackBar.open(
              'Unable to get patient details error ' + error.status,
              'OK'
            );
            this.cdr.detectChanges();
          }
        );
    } else {
      this.searchingPatientRecordsLoading = false;
    }
    this.cdr.detectChanges();
  }

  filterMenuItems(value: any) {
    if (typeof value === 'string' && value !== '') {
      this.menuItemsForSearch = this.flattenedMenuItems.filter((item) =>
        item.displayName.toLowerCase().includes(value.toLowerCase())
      );
    }
  }

  getInvoiceSummaries(value: string) {
    if (typeof value === 'string' && value != '' && value.includes('$')) {
      if (value.includes('-') && this.invoiceSummaries.length > 0) {
        // search the existing list
        this.flatMapTheInvoiceItem();
      } else {
        this.searchingPatientRecordsLoading = true;
        let searchTerm = value.replace('$', '');
        if (value.includes('-')) {
          searchTerm = value.split('-')[0].replace('$', '');
        }
        const httpParams = new HttpParams()
          .set('pn', this.patientPageNumber)
          .set('ps', this.patientPageSize)
          .set('st', searchTerm)
          .set('sot', '')
          .set('soo', '')
          .set('f', '');
        this.httpService
          .get<GenericResponse<GenericPaginatedData<EstimateView>>>(
            UrlConstants.invoiceSummaries,
            httpParams
          )
          .subscribe({
            next: (res) => {
              this.searchingPatientRecordsLoading = false;
              this.invoiceSummaries = res.Result?.ItemRecords || [];
              this.totalInvoiceItems = res.Result.TotalCount;
              if (res.Result?.ItemRecords?.length > 0) {
                this.flatMapTheInvoiceItem();
              } else {
                this.invoiceSearch = '';
              }
              this.cdr.detectChanges();
            },
            error: (err) => {
              this.searchingPatientRecordsLoading = false;
              this.insightService.logException(
                err,
                'main-layout.component',
                'error ',
                '675',
                SeverityLevel.Error
              );
              this.snackBar.open('Unable to get invoice summaries', 'OK', {
                duration: 6000,
              });
              this.cdr.detectChanges();
            },
          });
      }
    }
  }

  flatMapTheInvoiceItem() {
    this.filteredInvoiceSummaries = this.invoiceSummaries.flatMap((item) => {
      if (item.InvoiceDetails) {
        return item.InvoiceDetails.map((childItem) => {
          return {
            Id: item.Id,
            InvoiceRecordId: item.RecordId,
            InvoiceTypeId: item.InvoiceTypeId,
            ChildId: childItem.Id,
            ChildRecordId: childItem.RecordId,
            PatientDetailsId: item.PatientDetailsId,
          };
        }).filter((item) => {
          let isFilter = true;
          if (this.invoiceSearch && this.invoiceSearch.includes('-')) {
            const childRecordIdOnly = this.invoiceSearch.split('-')[1];
            if (childRecordIdOnly) {
              const globalRegex = new RegExp(childRecordIdOnly + '.*', 'g');
              return globalRegex.test(item.ChildRecordId.toString());
            }
          }
          return isFilter;
        });
      } else {
        return {
          Id: item.Id,
          InvoiceRecordId: item.RecordId,
          InvoiceTypeId: item.InvoiceTypeId,
          PatientDetailsId: item.PatientDetailsId,
        };
      }
    });
    this.searchingPatientRecordsLoading = false;
  }

  onAddPatient() {
    this.patientService.clearPatientDetails();
    this.router.navigateByUrl('/patient/add/patient_details');
    this.showSideNav = false;
  }

  onPatientLoadMoreClick() {
    this.patientPageSize += 5;
    this.filterPatientRecords(this.patientSearchTerm);
  }

  getPatientOtherDetails(patient: PatientDetails): string {
    let extraDetails = '';

    if (ProjectUtils.checkValueInString(patient.Mobile)) {
      extraDetails += patient.Mobile;
    }
    if (patient.DateofBirth) {
      if (extraDetails !== '') {
        extraDetails += ' | ';
      }
      extraDetails += ProjectUtils.getDateInDDMMYYY(patient.DateofBirth);
    }
    if (extraDetails !== '') {
      extraDetails += ' | ID ';
    } else {
      extraDetails += ' ID ';
    }

    extraDetails += patient.RecordId;

    return extraDetails;
  }

  onPatientClick(id: number) {
    const patientMenu = this.menuItems.find(
      (obj) => obj.displayName === 'Patients directory'
    );
    if (patientMenu) {
      this.setBackgroundColor(patientMenu.backgroundClass);
    }

    this.patientAutoCompleteFormCtrl.setValue('', { emitEvent: false });
    this.clearFilterText();
    // this.patientPrefferdName =null;

    // this.projectDataService.setPreferredName('');
    if (this.isPatientDashboardViewAllow) {
      this.router.navigateByUrl('/patient/' + id + '/dashboard');
    } else {
      this.router.navigateByUrl('/patient/' + id + '/activity_log');
    }

    this.showSideNav = false;
    this.cdr.detectChanges();
  }

  onLogout() {
    this.projectDataService.logout();
    this.cdr.detectChanges();
  }

  filterMenuClick(item: MenuItem) {
    item.clicked = true;

    this.showSideNav = !this.showSideNav;
    this.toggleMenuItem(item);

    this.hasSubMenu = !!item.subMenu;

    if (item.routeCompletePath !== '/search' && !item.subMenu) {
      this.menuItemsService.setMenuHeader({
        headerText: item.displayName,
        patientData: undefined,
      });
      this.router.navigateByUrl(`${item.routeCompletePath}`);
    }
    this.cdr.detectChanges();
  }

  clearFilterText() {
    this.patientAutoCompleteFormCtrl.setValue('', { emitEvent: false });
    this.clearItem();
  }

  clearItem() {
    this.searchingPatientRecordsLoading = false;
    this.menuItemsForSearch = [];
    this.patientRecords = [];
    this.patientRemainingCount = 0;
    this.filteredInvoiceSummaries = [];
    this.invoiceSummaries = [];
    this.totalInvoiceItems = 0;
    this.cdr.detectChanges();
  }

  openNote() {
    this.matDialog.open(NotpadModalComponent, {
      width: '350px',
      position: { left: '80px' },
    });
    this.cdr.detectChanges();
  }

  openInstantMessaging() {
    this.chatUserId = this.customStorageService.getItem(ProjectUtils.userIdLocKey) || '';
    this.chatUserName = this.customStorageService.getItem(ProjectUtils.usernameLocKey) || '';
    this.showChatWidget = true;
    this.cdr.detectChanges();
  }

  onChatMinimized() {
    this.showChatWidget = false;
    this.cdr.detectChanges();
  }

  private unreadPollInterval: any = null;

  private pollUnreadCount(): void {
    if (this.unreadPollInterval) return;
    this.unreadPollInterval = setInterval(() => {
      this.chatUnreadCount = this.chatStateService.totalUnread();
      this.cdr.detectChanges();
    }, 2000);
  }

  // PWA Installation
  addToHomeScreen() {
    this.projectDataService.addToHomeScreen();
    this.cdr.detectChanges();
  }

  onRefreshCache() {
    if (!this.isRefreshingCache) {
      this.isRefreshingCache = true;
      this.cacheService.resetConstants(true).subscribe({
        next: (res) => {
          this.snackBar.open('Cache refreshed...', 'OK', { duration: 6000 });
          this.isRefreshingCache = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.insightService.logException(
            err,
            'main-layout.component',
            'error ',
            '963',
            SeverityLevel.Error
          );
          this.snackBar.open('Failed to refresh cache', 'OK', {
            duration: 6000,
          });
          this.isRefreshingCache = false;
          this.cdr.detectChanges();
        },
      });
    }
  }

  initNotificationCount() {
    if (this.notificationCountSubscription) {
      this.notificationCountSubscription.unsubscribe();
    }
    this.notificationCountSubscription =
      this.menuItemsService.notificationCountSubject.subscribe((res) => {
        this.myActionNotificationCount = res.myActions;
         this.engagementNotificationCount = res.enquires;
        this.checklistNotificationsCount = res.checklists;
        this.patientActionNotificationCount = res.patientActions;
        this.cdr.detectChanges();
      });
  }

  initUserDetailsForProfilePic() {
    if (this.userDetailsSubscription) {
      this.userDetailsSubscription.unsubscribe();
    }
    this.userDetailsSubscription =
      this.projectDataService.loggedInUserSubject.subscribe((res) => {
        if (res) {
          this.userImage =
            res?.FileDetailsOutput?.SasTokens?.FileToken ||
            'assets/images/system-mgnt/users/avatar-child-face-thumbnail.png';
          this.isInstantMessagingEnabled = res?.IsInstantMessagingEnabled ?? false;
          if (this.isInstantMessagingEnabled) {
            this.pollUnreadCount();
          }
          this.cdr.detectChanges();
        }
      });
  }

  initRouteChangeEvents() {
    if (this.routeEventsSubscription) {
      this.routeEventsSubscription.unsubscribe();
    }
    this.routeEventsSubscription =
      this.projectDataService.routeInfoSubject.subscribe((res) => {
        this.setMenuHeaderAndBg(res.completeUrlSeg);
        this.cdr.detectChanges();
      });
  }

  initCustomMenuNameEvents() {
    if (this.menuHeaderSubscription) {
      this.menuHeaderSubscription.unsubscribe();
    }
    this.menuHeaderSubscription =
      this.menuItemsService.menuHeaderSubject.subscribe((res) => {
        this.headerData = res;
        this.cdr.detectChanges();
      });

    if (this.subMenuHeaderSubscription) {
      this.subMenuHeaderSubscription.unsubscribe();
    }
    this.subMenuHeaderSubscription =
      this.menuItemsService.menuSubHeaderSubject.subscribe((res) => {
        this.subHeader = res;
        this.cdr.detectChanges();
      });
  }

  /**
   * this function return a one dimensional pointer (reference) to the original menu list
   * hence any changes to the flattened array will be reflected across.
   * @param menuItemList
   * @param path
   */
  flattenMenuItems(menuItemList: MenuItem[], path: number[] = []): MenuItem[] {
    let itemList: MenuItem[] = [];
    for (let menuItem of menuItemList) {
      if (menuItem.subMenu?.length > 0) {
        path.push(menuItem.id);
        let childPath = [...path];
        itemList.push(...this.flattenMenuItems(menuItem.subMenu, childPath));
      }
      path = path.filter((item) => item != menuItem.id);
      menuItem.treePath = [...path];
      itemList.push(menuItem);
    }
    return itemList;
  }

  toggleMenuItem(selectedMenuItem: MenuItem) {
    let foundTreePath: number[] = [];
    this.flattenedMenuItems.forEach((item) => {
      if (item.id === selectedMenuItem.id) {
        item.clicked = true;
        foundTreePath = item.treePath;
      } else {
        item.clicked = false;
      }
      // toggle all item
    });
    let foundItem: MenuItem;
    foundTreePath.forEach((id) => {
      foundItem = this.flattenedMenuItems.find((item) => item.id === id);
      if (foundItem) {
        foundItem.clicked = true;
      }
    });
  }

  getBackgroundClass(selectedMenuItem: MenuItem) {
    let foundTreePath: number[] = [],
      selectedBgClass: string = '';
    for (let item of this.flattenedMenuItems) {
      if (item.id === selectedMenuItem.id) {
        foundTreePath = item.treePath;
        if (item.backgroundClass) {
          selectedBgClass = item.backgroundClass;
          return selectedBgClass;
        }
      }
    }
    let foundMenuItem: MenuItem;
    for (let pathId of foundTreePath) {
      foundMenuItem = this.flattenedMenuItems.find(
        (item) => item.id === pathId
      );
      if (foundMenuItem?.backgroundClass) {
        selectedBgClass = foundMenuItem.backgroundClass;
      }
    }
    return selectedBgClass;
  }

  private loadDPRCalendarFix() {
    if (window.location.hostname.includes('dpr.capstonesystem.com.au')) {
      const script = document.createElement('script');
      script.src = '/assets/js/dpr-calendar-header-fix.js';
      script.async = true;
      script.onload = () =>
        console.log('🔧 DPR calendar fix loaded successfully');
      script.onerror = () =>
        console.error('❌ Failed to load DPR calendar fix');
      document.head.appendChild(script);
    }
  }
}
